import React, { useState, useEffect } from 'react';
import { 
  FiTrendingUp, 
  FiTrendingDown, 
  FiDollarSign, 
  FiShoppingCart, 
  FiUsers, 
  FiCalendar,
  FiDownload,
  FiRefreshCw,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';
import { FaChartLine, FaMoneyBillWave, FaWallet, FaExchangeAlt } from 'react-icons/fa';
import './BusinessAnalytics.css';

interface AnalyticsData {
  summary: {
    total_received_usd: number;
    total_received_lrd: number;
    total_sent_usd: number;
    total_sent_lrd: number;
    salary_payouts_usd: number;
    salary_payouts_lrd: number;
    total_transactions: number;
    completed_transactions: number;
    pending_transactions: number;
    monthly_growth: number;
  };
  monthly_data: Array<{
    month: string;
    received_usd: number;
    received_lrd: number;
    sent_usd: number;
    sent_lrd: number;
  }>;
  top_customers: Array<{
    name: string;
    email: string;
    total_spent_usd: number;
    total_spent_lrd: number;
  }>;
  average_order_value_usd: number;
  average_order_value_lrd: number;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const BusinessAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [viewType, setViewType] = useState<'received' | 'sent'>('received');

  const token = localStorage.getItem('merchant_token');

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/merchants/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setAnalytics(data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const formatCurrency = (amount: number, currency: 'USD' | 'LRD' = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleExport = () => {
    if (!analytics) return;
    
    const csvContent = [
      ['Metric', 'Value'],
      ['Total Received (USD)', analytics.summary.total_received_usd],
      ['Total Received (LRD)', analytics.summary.total_received_lrd],
      ['Total Sent (USD)', analytics.summary.total_sent_usd],
      ['Total Sent (LRD)', analytics.summary.total_sent_lrd],
      ['Salary Payouts (USD)', analytics.summary.salary_payouts_usd],
      ['Salary Payouts (LRD)', analytics.summary.salary_payouts_lrd],
      ['Total Transactions', analytics.summary.total_transactions],
      ['Completed Transactions', analytics.summary.completed_transactions],
      ['Pending Transactions', analytics.summary.pending_transactions],
      ['Monthly Growth', `${analytics.summary.monthly_growth}%`],
      ['Average Order Value (USD)', analytics.average_order_value_usd],
      ['Average Order Value (LRD)', analytics.average_order_value_lrd],
      [],
      ['Top Customers'],
      ['Name', 'Email', 'Total Spent (USD)', 'Total Spent (LRD)'],
      ...analytics.top_customers.map(c => [c.name, c.email, c.total_spent_usd, c.total_spent_lrd])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getMaxValue = () => {
    if (!analytics) return 1;
    if (viewType === 'received') {
      return Math.max(...analytics.monthly_data.map(d => Math.max(d.received_usd, d.received_lrd / 200)));
    } else {
      return Math.max(...analytics.monthly_data.map(d => Math.max(d.sent_usd, d.sent_lrd / 200)));
    }
  };

  if (loading) {
    return (
      <div className="business-analytics">
        <div className="loading-analytics">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="business-analytics">
        <div className="empty-analytics">
          <p>No analytics data available</p>
        </div>
      </div>
    );
  }

  const maxValue = getMaxValue();

  return (
    <div className="business-analytics">
      <div className="analytics-header">
        <h2>
          <FaChartLine /> Business Analytics
        </h2>
        <div className="analytics-actions">
          <div className="view-toggle">
            <button 
              className={viewType === 'received' ? 'active' : ''} 
              onClick={() => setViewType('received')}
            >
              <FiArrowDown /> Received
            </button>
            <button 
              className={viewType === 'sent' ? 'active' : ''} 
              onClick={() => setViewType('sent')}
            >
              <FiArrowUp /> Sent
            </button>
          </div>
          <div className="period-selector">
            <button className={selectedPeriod === 'week' ? 'active' : ''} onClick={() => setSelectedPeriod('week')}>
              Week
            </button>
            <button className={selectedPeriod === 'month' ? 'active' : ''} onClick={() => setSelectedPeriod('month')}>
              Month
            </button>
            <button className={selectedPeriod === 'year' ? 'active' : ''} onClick={() => setSelectedPeriod('year')}>
              Year
            </button>
          </div>
          <button className="export-btn" onClick={handleExport}>
            <FiDownload /> Export
          </button>
          <button className="refresh-btn" onClick={fetchAnalytics} disabled={loading}>
            <FiRefreshCw className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
            <FiDollarSign />
          </div>
          <div className="stat-info">
            <span className="stat-label">Total Received (USD)</span>
            <h3>{formatCurrency(analytics.summary.total_received_usd, 'USD')}</h3>
            <span className="stat-trend positive">
              <FiTrendingUp /> +{analytics.summary.monthly_growth}%
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb, #f5576c)' }}>
            <FaMoneyBillWave />
          </div>
          <div className="stat-info">
            <span className="stat-label">Total Received (LRD)</span>
            <h3>{formatCurrency(analytics.summary.total_received_lrd, 'LRD')}</h3>
            <span className="stat-trend positive">
              <FiTrendingUp /> +{analytics.summary.monthly_growth}%
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
            <FaExchangeAlt />
          </div>
          <div className="stat-info">
            <span className="stat-label">Total Sent (USD)</span>
            <h3>{formatCurrency(analytics.summary.total_sent_usd, 'USD')}</h3>
            <span className="stat-trend negative">
              <FiTrendingDown /> Outgoing
            </span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b, #ea580c)' }}>
            <FaWallet />
          </div>
          <div className="stat-info">
            <span className="stat-label">Salary Payouts</span>
            <h3>{formatCurrency(analytics.summary.salary_payouts_usd, 'USD')}</h3>
            <span className="stat-subtitle">+ {formatCurrency(analytics.summary.salary_payouts_lrd, 'LRD')}</span>
          </div>
        </div>
      </div>

      <div className="stats-secondary">
        <div className="stat-card small">
          <div className="stat-info">
            <span className="stat-label">Total Transactions</span>
            <h3>{analytics.summary.total_transactions}</h3>
          </div>
        </div>
        <div className="stat-card small">
          <div className="stat-info">
            <span className="stat-label">Completed</span>
            <h3 className="text-green">{analytics.summary.completed_transactions}</h3>
          </div>
        </div>
        <div className="stat-card small">
          <div className="stat-info">
            <span className="stat-label">Pending</span>
            <h3 className="text-yellow">{analytics.summary.pending_transactions}</h3>
          </div>
        </div>
        <div className="stat-card small">
          <div className="stat-info">
            <span className="stat-label">Avg Order Value</span>
            <h3>{formatCurrency(analytics.average_order_value_usd, 'USD')}</h3>
            <span className="stat-subtitle">{formatCurrency(analytics.average_order_value_lrd, 'LRD')}</span>
          </div>
        </div>
      </div>

      <div className="charts-section">
        <div className="chart-card">
          <h3>{viewType === 'received' ? 'Money Received' : 'Money Sent'} Trend</h3>
          <div className="bar-chart">
            {analytics.monthly_data.map((data, index) => {
              let value = 0;
              if (viewType === 'received') {
                value = (data.received_usd + data.received_lrd / 200);
              } else {
                value = (data.sent_usd + data.sent_lrd / 200);
              }
              const height = (value / maxValue) * 200;
              
              return (
                <div key={index} className="bar-item">
                  <div className="bar-label">{data.month}</div>
                  <div 
                    className={`bar ${viewType === 'received' ? 'bar-received' : 'bar-sent'}`}
                    style={{ height: `${Math.min(height, 200)}px` }}
                  >
                    <span className="bar-value">${(value / 1000).toFixed(1)}k</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="top-customers-card">
          <h3>Top Customers</h3>
          <div className="customers-list">
            {analytics.top_customers.slice(0, 5).map((customer, index) => (
              <div key={index} className="customer-item">
                <div className="customer-avatar">
                  {customer.name.charAt(0).toUpperCase()}
                </div>
                <div className="customer-info">
                  <div className="customer-name">{customer.name}</div>
                  <div className="customer-email">{customer.email}</div>
                </div>
                <div className="customer-amount">
                  {formatCurrency(customer.total_spent_usd, 'USD')}
                  {customer.total_spent_lrd > 0 && (
                    <span className="lrd-amount"> | {formatCurrency(customer.total_spent_lrd, 'LRD')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="growth-metrics">
        <div className="growth-card">
          <FiCalendar />
          <div>
            <h4>Monthly Growth</h4>
            <p className={analytics.summary.monthly_growth >= 0 ? 'positive' : 'negative'}>
              {analytics.summary.monthly_growth >= 0 ? '+' : ''}{analytics.summary.monthly_growth}%
            </p>
          </div>
        </div>
        <div className="growth-card">
          <FiShoppingCart />
          <div>
            <h4>Completion Rate</h4>
            <p className="positive">
              {Math.round((analytics.summary.completed_transactions / analytics.summary.total_transactions) * 100)}%
            </p>
          </div>
        </div>
        <div className="growth-card">
          <FiUsers />
          <div>
            <h4>Unique Customers</h4>
            <p>{analytics.top_customers.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessAnalytics;
